import { useReady } from '../utils/dom.js';

import { LEAD_TYPE_1_TO_ATTRIBUTION, LEAD_TYPE_2_TO_ATTRIBUTION } from './const.js';
import { useFormAbandonment } from './form-abandonment.js';

import {
  useSubmitButton,
  useFormSend,
  useFormError,
  useInputMasks,
  useDynamicSelects,
  useDynamicLeadAttribution,
  usePixel,
  useValidDate,
  useInputCustomError,
  formatLoggingError,
  useOtherInput,
} from './form.js';
import { useAttribution, useCriteoSuccessTag } from './form-attribution.js';
import { useChatbotAttribution } from './chatbot-attribution.js';
import { trackFormSubmit, trackFormConversion, trackFormError } from '../tracking/index.js';
import { useTranslation } from '../translate/index.js';
import { getOfferText } from './offer-text.js';

// Keep track of initialized forms to prevent double initialization
const initializedForms = new Set();

const useConversions = (form) => {
  trackFormSubmit(form);

  // Fire custom event, this is hooked onto in GTM with:
  // - "Custom Event - Form Conversion" trigger
  // - Fires conversion tag "AdWords - Conversion - Appt"
  trackFormConversion(form);

  // Yelp tracking pixel
  usePixel(
    'https://d.adroll.com/ipixel/4XHBFKJ5DNG2JHATNQ2UBK/UMWYH2HCQZEYXDLSUXBUMN?name=656e1fcc&ar_ed_order_id='
  );

  // Facebook lead tracking
  if (window.fbq) {
    fbq('track', 'Lead', {
      content_name: 'Book an appointment',
      content_category: document.referrer,
    });
  }
};

const useForm = (form, attribution) => {
  // Check if form is already initialized
  if (initializedForms.has(form)) {
    // console.log('Form already initialized, skipping');
    return;
  }
  initializedForms.add(form);

  // console.log('Initializing form with abandonment tracking');
  // console.log('use form', form, attribution);
  // Submit button helpers
  const { setText, enable, disable } = useSubmitButton(
    form.querySelector('[type="submit"]')
  );

  // Error message helper
  const { setError, clearError } = useFormError(
    form.querySelector('[data-target="form-fail"]')
  );

  // Timeout form after a day
  // clear timeout...
  const formTimer = setTimeout(() => {
    disable();
    setError(
      'The form has timed out. Please refresh the page and submit the form within 12 hours.'
    );
  }, 1000 * 60 * 60 * 12);

  // Input masks for dates and phone numbers
  useInputMasks({
    dateSelector: '[data-mask="date"]',
    phoneSelector: '[data-mask="phone"]',
  });

  // Validate date to YYYY-MM-DD
  useValidDate(form.elements.birthday);

  // Custom error messages
  useInputCustomError(form.querySelectorAll('[data-error-message]'));

  // Dynamic location select based on treatment
  // Show/hide "Book Online" LASIK Appointment button
  useDynamicSelects(form);

  // Dynamic lead type to lead attribution
  useDynamicLeadAttribution(
    form.elements.lead_type,
    form.elements.lead_attribution,
    LEAD_TYPE_1_TO_ATTRIBUTION
  );
  useDynamicLeadAttribution(
    form.elements.lead_type_2,
    form.elements.lead_attribution_2,
    LEAD_TYPE_2_TO_ATTRIBUTION
  );

  // Other input on lead attribution for key vendor lead form
  useOtherInput({
    input: form.elements.lead_attribution, // input to listen for change events on
    target: form.querySelector('[data-other-input="lead_attribution"]'), // target to toggle visibility on
    targetInput: form.elements.lead_attribution_other, // input to clear when hidden
  });

  // Get language
  const { setLanguageInput } = useTranslation(form);

  // If this is a Criteo attributed form, load Criteo
  let fireCriteoSuccessTag;
  if (attribution === 'Criteo') {
    const { fire } = useCriteoSuccessTag();
    fireCriteoSuccessTag = fire;
  }

  // Add form abandonment tracking
  // console.log('Setting up form abandonment tracking');
  useFormAbandonment(form);
  // console.log('Form abandonment tracking setup complete');

  // Make form sender
  const { send } = useFormSend({
    form,
    onBefore: () => {
      // console.log('contact submit - sending...');
      const isQuizForm = form.getAttribute('data-type') === 'Step';
      setText(isQuizForm ? 'Submitting...' : 'Booking Appointment...');
      clearError();
      disable();
      setLanguageInput();
    },
    onSuccess: (response) => {
      // console.info('contact submit - success', response);
      const isBook = response?.data?.book;

      // Clear form timeout
      clearTimeout(formTimer);

      // Dispatch form-submit-success event for tracking
      form.dispatchEvent(new Event('form-submit-success'));

      if (isBook) {
        // Run form conversions
        useConversions(form);

        // If using Criteo fire success tag
        fireCriteoSuccessTag && fireCriteoSuccessTag();
      }

      let successHtml = '';
      const customSuccessHtml = form.querySelector('[data-target="success-message"]');
      if (customSuccessHtml) {
        successHtml = customSuccessHtml.innerHTML;
      } else if (form.getAttribute('data-type') === 'Step') {
        // Quiz-specific success message
        const isDq = form.querySelector('[data-target="dq-text"]')?.classList.contains('hidden') === false;
        const candidateTitle = form.querySelector('[data-target="candidate-text"]')?.innerHTML;
        const dqTitle = form.querySelector('[data-target="dq-text"]')?.innerHTML;
        const candidateContent = form.querySelector('[data-target="candidate-text"]')?.nextElementSibling?.innerHTML;
        const dqContent = form.querySelector('[data-target="dq-text"]')?.nextElementSibling?.innerHTML;

        successHtml = `
          <div class="text-center">
            <h3 class="Heading-4 mb-4">
              <span data-target="candidate-text">${candidateTitle || 'Congratulations, you may be a good candidate for LASIK!'}</span>
              <span data-target="dq-text" class="hidden">${dqTitle || 'Looks like another vision correction procedure may be a better fit for you. Ready to book a no-obligation consultation?'}</span>
            </h3>
            <p class="text-lg">
              <span data-target="candidate-text">${candidateContent || 'One of our LASIK specialists will contact you shortly to discuss your results and schedule your free, no-pressure LASIK consultation.'}</span>
              <span data-target="dq-text" class="hidden">${dqContent || 'Even though you may not be the typical candidate for LASIK, don\'t worry – we offer a wide range of vision correction options to help you see better. One of our vision correction specialists will contact you shortly to discuss your options to achieve the crisp, clear vision you deserve. Ready to book a no-obligation consultation?'}</span>
            </p>
            <div class="mt-6">
              <a href="/book-an-appointment/" class="Button Button--YellowSolid">Book Now</a>
            </div>
          </div>
        `;
      } else {
        let message = form.getAttribute('data-success-message') ||
          `Thank you! We've received your request and will get back to you shortly.`;
        successHtml = `<p class="Heading-4">${message}</p>`;
      }

      form.innerHTML = successHtml;
    },
    onFail: (response) => {
      const { data } = response;
      // console.error('contact submit - fail', response);
      setText('Book an Appointment');
      enable();
      const message =
        (data && data.message) ||
        'Oops, sorry about that, there was an issue submitting the form. Please check the fields and try again.';
      setError(message);

      const errorType = data.error_type;

      // Do not report validation errors to Sentry
      if (errorType === 'validation') {
        return;
      }

      let loggingError =
        data && data.response && data.response.length
          ? formatLoggingError(data.response)
          : message;
      if (errorType) {
        loggingError = `${errorType} - ${loggingError}`;
      }
      loggingError = `Booking form error - ${loggingError}`;
      // Sentry.captureMessage(loggingError);

      trackFormError(form, {
        error: loggingError,
      });
    },
  });

  // On submit, send data
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    send().catch((e) => {
      // console.log('Send error', e);
    });
  });
};

const handleSubmit = async (e) => {
  e.preventDefault();

  const form = e.target;
  const isQuizForm = form.getAttribute('data-type') === 'Step';
  const submitBtn = form.querySelector('[data-action="submit"]');
  const loadingText = isQuizForm ? 'Submitting...' : 'Booking Appointment...';

  // Update button text while submitting
  if (submitBtn) {
    submitBtn.innerHTML = loadingText;
    submitBtn.disabled = true;
  }

  try {
    await submitForm(form);

    // Clear any existing errors
    clearErrors(form);

    // Hide form
    form.classList.add('hidden');

    // Show success message
    const successEl = form.querySelector('[data-target="success-message"]');
    if (successEl) {
      successEl.classList.remove('hidden');
    } else {
      // Only show default message if no custom success message exists
      const defaultSuccess = document.createElement('div');
      defaultSuccess.className = 'text-center';
      defaultSuccess.innerHTML = `
        <h3 class="Heading-3 mb-4">Thank you! We've received your request and will get back to you shortly.</h3>
      `;
      form.parentNode.insertBefore(defaultSuccess, form.nextSibling);
    }

  } catch (error) {
    // ... existing error handling ...
  } finally {
    if (submitBtn) {
      const originalText = isQuizForm
        ? submitBtn.querySelector('[data-target="candidate-text"]')?.innerHTML
        : 'Book Appointment';
      submitBtn.innerHTML = originalText;
      submitBtn.disabled = false;
    }
  }
};

// On DOM ready, setup forms and form attribution
useReady(() => {
  // console.log('Form module: DOM ready - Starting initialization');

  // Function to initialize a form
  const initializeForm = (form) => {
    // console.log('Form module: Found form element:', form ? 'Yes' : 'No');
    if (form) {
      const formType = form.getAttribute('data-type');
      const formName = form.getAttribute('data-name');

      // console.log('Form module: Form details:', {
      //   id: form.id,
      //   type: formType,
      //   name: formName,
      //   elements: form.elements.length,
      //   html: form.outerHTML.substring(0, 200) + '...' // First 200 chars of form HTML
      // });

      try {
        // Initialize form abandonment tracking
        // console.log('Form module: Initializing form abandonment tracking');
        useFormAbandonment(form);
        // console.log('Form module: Form abandonment tracking initialized');

        // Get attribution fields
        const { attributionFields } = useAttribution(form);
        // console.log('Form module: Attribution fields:', attributionFields);

        // Initialize the form based on its type
        if (formType === 'Step' || form.getAttribute('data-form-submit') === 'true') {
          // console.log('Form module: Initializing form type:', formType);
          useForm(form, attributionFields.lead_channel);
        } else {
          // console.warn('Form module: Unknown form type:', formType);
        }

        // console.log('Form module: Form initialization complete');
      } catch (error) {
        // console.error('Form module: Error during initialization:', error);
      }
    }
  };

  // Initialize any existing forms
  const existingForm = document.querySelector('[data-plugin="nv-form"]');
  // console.log('Form module: Checking for existing forms:', existingForm ? 'Found' : 'Not found');

  if (existingForm) {
    initializeForm(existingForm);
  }

  // Set up MutationObserver to watch for dynamically added forms
  const observer = new MutationObserver((mutations) => {
    // console.log('Form module: DOM mutation detected');
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is a form
          if (node.matches('[data-plugin="nv-form"]')) {
            // console.log('Form module: New form detected in DOM');
            initializeForm(node);
          }
          // Check if the added node contains a form
          const forms = node.querySelectorAll('[data-plugin="nv-form"]');
          if (forms.length > 0) {
            // console.log('Form module: Found', forms.length, 'forms in added content');
            forms.forEach((form) => {
              // console.log('Form module: New form detected in added content');
              initializeForm(form);
            });
          }
        }
      });
    });
  });

  // Start observing the document body for added nodes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // console.log('Form module: MutationObserver setup complete');
});
